import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import {
  modalsActions,
  selectDeleteEvent,
  selectDeleteAssistEvent,
  selectDeleteSubstituteEvent,
  selectDeleteFouledEvent,
  selectDeleteOffenceEvent,
  selectDeletePenaltySaveEvent,
  selectDeletePenaltyMissEvent,
  selectDeletePenaltyGoalEvent,
} from 'features/modals/modalsSlice';
import { annulMatchEvent, deleteMatchEvent, resetToBeforeMatch } from 'features/match/thunks';

import Modal from 'atoms/Modal';
import { ModalProps } from 'atoms/Modal/ModalTypes';

import Button, { SecondaryButton } from 'styled/components/Button';
import StyledDeleteEventModal from './StyledDeleteEventModal';
import classNames from 'classnames';
import { matchSliceActions, selectActiveMatchEvents } from '../../../features/match/matchSlice';
import { MatchEventType } from '../../../enums/matchEventType';
import { inputSliceActions } from '../../../features/input/inputSlice';
import { useAppDispatch } from '../../../hooks/hooks';

const DeleteEventModal: React.FC<ModalProps> = (props) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();

  const matchEvent = useSelector(selectDeleteEvent);
  const assistEvent = useSelector(selectDeleteAssistEvent);
  const substituteEvent = useSelector(selectDeleteSubstituteEvent);
  const fouledEvent = useSelector(selectDeleteFouledEvent);
  const offenceEvent = useSelector(selectDeleteOffenceEvent);
  const penaltySaveEvent = useSelector(selectDeletePenaltySaveEvent);
  const penaltyMissEvent = useSelector(selectDeletePenaltyMissEvent);
  const penaltyGoalEvent = useSelector(selectDeletePenaltyGoalEvent);
  const activeMatchEvents = useSelector(selectActiveMatchEvents);

  let isGoalEvent = false;
  let id = 'modals.deleteEvent';

  if (matchEvent) {
    isGoalEvent =
      matchEvent.matchEventTypeId === MatchEventType.GOAL || matchEvent.matchEventTypeId === MatchEventType.OWN_GOAL;
    if (isGoalEvent) {
      id = 'modals.deleteOrAnnulEvent';
    }
  }

  const deleteTheEvent = () => {
    if (matchEvent) {
      let foundEvents = activeMatchEvents.filter((activeMatchEvent) => activeMatchEvent.id === matchEvent.id);
      if (foundEvents.length === 0 && matchEvent.matchEventTypeId === MatchEventType.PENALTY) {
        foundEvents = activeMatchEvents.filter(
          (activeMatchEvent) => activeMatchEvent.matchEventTypeId === MatchEventType.PENALTY_MISS,
        );
      }
      if (foundEvents.length > 0) {
        dispatch(inputSliceActions.newEvent());
        dispatch(matchSliceActions.clearActiveMatchEvents());
      }

      if (matchEvent.matchEventTypeId !== MatchEventType.MATCH_KICKS_OFF) {
        dispatch(deleteMatchEvent(matchEvent));
      }

      if (assistEvent) {
        dispatch(deleteMatchEvent(assistEvent));
      }

      if (substituteEvent) {
        dispatch(deleteMatchEvent(substituteEvent));
      }

      if (fouledEvent && matchEvent.matchEventTypeId !== MatchEventType.PENALTY_MISS) {
        dispatch(deleteMatchEvent(fouledEvent));
      }

      if (offenceEvent && matchEvent.matchEventTypeId !== MatchEventType.PENALTY_MISS) {
        dispatch(deleteMatchEvent(offenceEvent));
      }

      if (
        penaltySaveEvent &&
        (matchEvent.matchEventTypeId === MatchEventType.PENALTY_MISS ||
          matchEvent.matchEventTypeId === MatchEventType.PENALTIES_MISS)
      ) {
        dispatch(deleteMatchEvent(penaltySaveEvent));
      }

      if (penaltyMissEvent && matchEvent.matchEventTypeId === MatchEventType.PENALTY) {
        dispatch(deleteMatchEvent(penaltyMissEvent));
      }

      if (penaltyGoalEvent) {
        dispatch(deleteMatchEvent(penaltyGoalEvent));
      }

      if (matchEvent.matchEventTypeId === MatchEventType.MATCH_KICKS_OFF) {
        dispatch(resetToBeforeMatch());
      }
    }
    dispatch(modalsActions.closeDeleteEventModal());
  };

  const annulEvent = () => {
    if (matchEvent) {
      dispatch(annulMatchEvent(matchEvent));
      dispatch(inputSliceActions.setActiveEventTypeId(MatchEventType.DISALLOWED_GOAL));
      if (assistEvent) {
        dispatch(deleteMatchEvent(assistEvent));
      }
    }
    dispatch(modalsActions.closeDeleteEventModal());
  };

  return (
    <Modal {...props}>
      <StyledDeleteEventModal>
        <FormattedMessage id={id} />
        <div
          className={classNames('actions', {
            isGoalEvent,
          })}
        >
          <SecondaryButton whiteBackground greenText onClick={deleteTheEvent}>
            {intl.formatMessage({ id: 'modals.delete' })}
          </SecondaryButton>
          {isGoalEvent && <Button onClick={annulEvent}>{intl.formatMessage({ id: 'modals.annul' })}</Button>}
        </div>
      </StyledDeleteEventModal>
    </Modal>
  );
};

export default DeleteEventModal;
